import { useMemo } from 'react';

import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import 'highlight.js/styles/github.css';

import { Avatar } from '../shared/Avatar';
import { useUserContext } from '../../contexts/UserContext';
import { ChatMessage, ChatRole } from '../../types/Orbit/Chat';
import useOrganisationUsers from '../../data/organisationUsers/useOrganisationUsers';

interface ChatBubbleProps {
  message: ChatMessage;
}

export function ChatBubble({ message }: ChatBubbleProps) {
  const { userId } = useUserContext();
  const { users } = useOrganisationUsers();
  const isUser = message.role === ChatRole.User;

  const user = useMemo(() => {
    if (!users.data || !message.userId) return null;
    return users.data.find((u) => u.id === message.userId);
  }, [users.data, message.userId]);

  return (
    <div
      className={clsx(
        'flex items-end gap-1 w-full',
        isUser
          ? 'justify-end animate-[bubble-from-right_0.25s_ease-out_both]'
          : 'justify-start animate-[bubble-from-left_0.25s_ease-out_both]',
      )}
    >
      {isUser ? (
        user ? (
          <Avatar {...user} size={6} />
        ) : (
          <div className="w-6 h-6 shrink-0" />
        )
      ) : null}
      <div
        className={clsx(
          'max-w-[75%] px-4 py-3 rounded-xl text-sm wrap-break-word',
          isUser
            ? 'bg-primary text-primaryContrast rounded-br-sm'
            : 'bg-gray-100 dark:bg-gray-600 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-sm',
        )}
      >
        <div
          className={clsx(
            'prose prose-sm max-w-none leading-snug',
            isUser ? 'prose-invert' : 'dark:prose-invert'
          )}
        >
          {isUser ? (
            message.content
          ) : (
            <ReactMarkdown
              children={message.content}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[
                rehypeRaw,
                rehypeSanitize,
                rehypeSlug,
                rehypeAutolinkHeadings,
                rehypeHighlight,
              ]}
              components={{
                ul: ({ ...props }) => <ul className="list-disc list-inside my-4" {...props} />,
                ol: ({ ...props }) => <ol className="list-decimal list-inside" {...props} />,
                li: ({ ...props }) => <li className="mb-1" {...props} />,
                a: ({ ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                img: ({ ...props }) => (
                  <img className="rounded-lg shadow-sm mx-auto max-w-full" {...props} />
                ),
                code: ({ node, className, children, ...props }) => {
                  if ((node as unknown as { inline?: boolean })?.inline) {
                    return (
                      <code
                        className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-sm"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                  return (
                    <pre className="bg-gray-900 text-white rounded-lg p-3 overflow-auto">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  );
                },
                table: ({ ...props }) => (
                  <div className="my-2 overflow-auto rounded-md shadow-sm">
                    <table
                      className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-auto border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                      {...props}
                    />
                  </div>
                ),
                thead: ({ ...props }) => (
                  <thead className="bg-gray-50 dark:bg-gray-700" {...props} />
                ),
                tbody: ({ ...props }) => (
                  <tbody
                    className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700"
                    {...props}
                  />
                ),
                tr: ({ ...props }) => (
                  <tr
                    className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700"
                    {...props}
                  />
                ),
                th: ({ ...props }) => (
                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider"
                    {...props}
                  />
                ),
                td: ({ ...props }) => (
                  <td
                    className="px-3 py-2 text-sm text-gray-700 dark:text-gray-100 align-top"
                    {...props}
                  />
                ),
              }}
            />
          )}
        </div>
        <span className="block mt-1 text-xs opacity-60 text-right">
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
}
